import { Card, Col, Form, Radio, Row, Spin, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useGetUnit } from "../hooks/use-get-unit";
import Input from "antd/es/input";
import VuiFormActionButton from "../../../components/form-action-button";
import { useCallback, useEffect, useState } from "react";
import VuiPageTitle from "../../../components/page-title";
import { useFormUnit } from "../hooks/use-form-unit";
import { UnitFormData, UnitPayloadType } from "./interface";
import { openNotification } from "../../../utils/helpers";
import VuiAccessible from "../../../components/accessible";
import VuiModalConfirmation from "../../../components/modal-confirmation";

const { TextArea } = Input;
const { Text } = Typography;

const UnitFormModule = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const title = id ? t("pages.unit.detail.title") : t("pages.unit.add.title");
  const { loadUnitData, isOnFetchingUnitData, unitData } = useGetUnit();
  const { submitLoading, onSubmit, onDelete, deleteLoading } = useFormUnit();
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const [form] = Form.useForm<UnitFormData>();

  const onFinish = useCallback(
    async (data: UnitFormData) => {
      const normalizeData: UnitPayloadType = {
        ...data,
      };

      await onSubmit(normalizeData, id);
    },
    [id, onSubmit]
  );

  const handleCloseModalDelete = useCallback(() => {
    setShowDeleteModal(false);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (id) {
      await onDelete(id).then(() => {
        setShowDeleteModal(false);
        openNotification(
          "success",
          t("notification.success.deleteItem", {
            item: t("common.text.unit"),
          })
        );
        navigate(`/other-list/unit`);
      });
    }
  }, [id, navigate, onDelete, t]);

  useEffect(() => {
    if (id) {
      (async () => {
        await loadUnitData(id);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (id && unitData) {
      form.setFieldsValue({
        ...unitData,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitData]);

  return (
    <>
      <VuiPageTitle title={title} onBackLink="/other-list/unit" />

      <Spin size="large" spinning={isOnFetchingUnitData}>
        <Form
          form={form}
          layout={"vertical"}
          initialValues={{
            is_active: true,
          }}
          onFinish={onFinish}
        >
          <Row gutter={[16, 16]}>
            <Col md={18} xs={24}>
              <Card title={t("common.text.information")}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="name"
                      label={t("common.form.unitName.label")}
                      rules={[
                        {
                          required: true,
                          message: t("validation.required", {
                            item: t("common.form.unitName.label"),
                          }),
                        },
                      ]}
                    >
                      <Input
                        size="large"
                        placeholder={t("common.form.unitName.placeholder")}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>
            </Col>

            <Col md={6} xs={24}>
              <Card title={t("common.text.status")}>
                <Form.Item name="is_active">
                  <Radio.Group>
                    <Radio value={true}>Active</Radio>
                    <Radio value={false}>Not Active</Radio>
                  </Radio.Group>
                </Form.Item>
              </Card>
            </Col>

            <Col md={24} xs={24}>
              <VuiFormActionButton
                cancelBtnLink="/other-list/unit"
                isLoading={submitLoading}
              />
            </Col>

            {id && (
              <VuiAccessible access="unit.destroy">
                <Col xs={24}>
                  <Text
                    className="cursor-pointer"
                    onClick={() => setShowDeleteModal(true)}
                    title={t("common.text.delete")}
                    type="secondary"
                  >
                    {t("common.text.delete")}
                  </Text>
                </Col>
              </VuiAccessible>
            )}
          </Row>
        </Form>
      </Spin>

      <VuiModalConfirmation
        show={showDeleteModal}
        isLoading={deleteLoading}
        onSubmit={handleConfirmDelete}
        onCancel={handleCloseModalDelete}
      />
    </>
  );
};

export default UnitFormModule;
